import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {Link as ScrollTo} from 'react-scroll';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SystemDiagram from './SystemDiagram';

const HERO_DIAGRAM_SRC = 'https://images.happysolutions.hu/Happy-Gastro-System-Diagram-2023.webp';

/**
 * Smooth cursor-following mesh gradient overlay.
 * Tracks the cursor with eased follow (lerp) so it feels alive but not jittery.
 */
const HeroMeshGradient = ({ scopeRef }) => {
    const blobRef = useRef(null);
    const stateRef = useRef({
        x: 0, y: 0, tx: 0, ty: 0, vel: 0, active: false, raf: 0,
    });

    useEffect(() => {
        const scope = scopeRef.current;
        if (!scope || !blobRef.current) return undefined;

        // Init centered
        const rect0 = scope.getBoundingClientRect();
        stateRef.current.x = rect0.width * 0.5;
        stateRef.current.y = rect0.height * 0.4;
        stateRef.current.tx = stateRef.current.x;
        stateRef.current.ty = stateRef.current.y;

        const tick = () => {
            const s = stateRef.current;
            // Faster ease when far, slower when close → friendly "weighted" feel
            const dx = s.tx - s.x;
            const dy = s.ty - s.y;
            const dist = Math.hypot(dx, dy);
            const ease = Math.min(0.18, 0.045 + dist / 2800);
            s.x += dx * ease;
            s.y += dy * ease;
            s.vel = s.vel * 0.85 + dist * 0.15;
            const intensity = Math.min(1, s.vel / 120);
            const scale = 1 + (s.active ? 0.05 : 0) + intensity * 0.15;
            const blob = blobRef.current;
            if (blob) {
                blob.style.transform = `translate3d(${s.x - 320}px, ${s.y - 320}px, 0) scale(${scale.toFixed(3)})`;
                blob.style.opacity = s.active ? `${(0.72 + intensity * 0.18).toFixed(3)}` : '0.32';
            }
            s.raf = requestAnimationFrame(tick);
        };

        const onMove = (e) => {
            const rect = scope.getBoundingClientRect();
            const p = e.touches ? e.touches[0] : e;
            stateRef.current.tx = p.clientX - rect.left;
            stateRef.current.ty = p.clientY - rect.top;
            stateRef.current.active = true;
        };
        const onLeave = () => {
            stateRef.current.active = false;
        };

        scope.addEventListener('mousemove', onMove, { passive: true });
        scope.addEventListener('touchmove', onMove, { passive: true });
        scope.addEventListener('mouseleave', onLeave);
        scope.addEventListener('touchend', onLeave);
        stateRef.current.raf = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(stateRef.current.raf);
            scope.removeEventListener('mousemove', onMove);
            scope.removeEventListener('touchmove', onMove);
            scope.removeEventListener('mouseleave', onLeave);
            scope.removeEventListener('touchend', onLeave);
        };
    }, [scopeRef]);

    return (
        <div className="premium-hero__cursor-mesh" aria-hidden="true">
            <div ref={blobRef} className="premium-hero__cursor-blob" />
        </div>
    );
};

/**
 * Interactive system diagram:
 * - Cursor-driven zoom-on-hover with smooth lerp toward the cursor
 * - Lightweight magnifier "lens" overlay at the pointer
 * - Click to open immersive fullscreen pan/zoom mode
 */
const InteractiveDiagram = ({ alt }) => {
    const { t } = useTranslation();
    const wrapRef = useRef(null);
    const imgRef = useRef(null);
    const lensRef = useRef(null);
    const rafRef = useRef(0);
    const stateRef = useRef({
        hovered: false, mx: 0.5, my: 0.5, tx: 0.5, ty: 0.5, scale: 1, tScale: 1,
    });

    const [fullscreen, setFullscreen] = useState(false);
    const [fsHintVisible, setFsHintVisible] = useState(false);
    const fsStageRef = useRef(null);
    const fsBlobRef = useRef(null);
    const fsRafRef = useRef(0);
    const fsStateRef = useRef({ mx: 0.5, my: 0.5, tx: 0.5, ty: 0.5 });

    // -- inline interactive zoom / pan --
    useEffect(() => {
        const wrap = wrapRef.current;
        const img = imgRef.current;
        const lens = lensRef.current;
        if (!wrap || !img) return undefined;

        let rect = wrap.getBoundingClientRect();
        const measure = () => { rect = wrap.getBoundingClientRect(); };
        const ro = ('ResizeObserver' in window) ? new ResizeObserver(measure) : null;
        if (ro) ro.observe(wrap);
        window.addEventListener('resize', measure);

        const tick = () => {
            const s = stateRef.current;
            s.mx += (s.tx - s.mx) * 0.12;
            s.my += (s.ty - s.my) * 0.12;
            s.scale += (s.tScale - s.scale) * 0.12;
            const dx = (s.mx - 0.5) * 14; // % translate
            const dy = (s.my - 0.5) * 12;
            img.style.transformOrigin = `${(s.mx * 100).toFixed(2)}% ${(s.my * 100).toFixed(2)}%`;
            img.style.transform = `translate3d(${-dx}%, ${-dy}%, 0) scale(${s.scale.toFixed(3)})`;
            if (lens) {
                lens.style.opacity = s.hovered ? '1' : '0';
                const lx = s.mx * rect.width;
                const ly = s.my * rect.height;
                lens.style.transform = `translate3d(${lx.toFixed(1)}px, ${ly.toFixed(1)}px, 0) translate(-50%, -50%)`;
            }
            rafRef.current = requestAnimationFrame(tick);
        };

        const onMove = (e) => {
            const p = e.touches ? e.touches[0] : e;
            stateRef.current.tx = Math.max(0, Math.min(1, (p.clientX - rect.left) / rect.width));
            stateRef.current.ty = Math.max(0, Math.min(1, (p.clientY - rect.top) / rect.height));
        };
        const onEnter = () => {
            stateRef.current.hovered = true;
            stateRef.current.tScale = 1.35;
        };
        const onLeave = () => {
            stateRef.current.hovered = false;
            stateRef.current.tScale = 1;
            stateRef.current.tx = 0.5;
            stateRef.current.ty = 0.5;
        };

        wrap.addEventListener('mousemove', onMove, { passive: true });
        wrap.addEventListener('touchmove', onMove, { passive: true });
        wrap.addEventListener('mouseenter', onEnter);
        wrap.addEventListener('mouseleave', onLeave);
        wrap.addEventListener('touchend', onLeave);
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(rafRef.current);
            if (ro) ro.disconnect();
            window.removeEventListener('resize', measure);
            wrap.removeEventListener('mousemove', onMove);
            wrap.removeEventListener('touchmove', onMove);
            wrap.removeEventListener('mouseenter', onEnter);
            wrap.removeEventListener('mouseleave', onLeave);
            wrap.removeEventListener('touchend', onLeave);
        };
    }, []);

    // -- fullscreen keyboard / scroll --
    useEffect(() => {
        if (!fullscreen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setFullscreen(false);
        };
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', onKey);
        };
    }, [fullscreen]);

    // -- fullscreen: hint auto-show then fade --
    useEffect(() => {
        if (!fullscreen) { setFsHintVisible(false); return undefined; }
        const tIn = setTimeout(() => setFsHintVisible(true), 220);
        const tOut = setTimeout(() => setFsHintVisible(false), 3800);
        return () => { clearTimeout(tIn); clearTimeout(tOut); };
    }, [fullscreen]);

    // -- fullscreen: ambient backdrop blob follows the cursor --
    useEffect(() => {
        if (!fullscreen) return undefined;
        const stage = fsStageRef.current;
        const blob = fsBlobRef.current;
        if (!stage) return undefined;

        let rect = stage.getBoundingClientRect();
        const measure = () => { rect = stage.getBoundingClientRect(); };
        window.addEventListener('resize', measure);

        const tick = () => {
            const s = fsStateRef.current;
            s.mx += (s.tx - s.mx) * 0.08;
            s.my += (s.ty - s.my) * 0.08;
            if (blob) {
                const bx = s.mx * rect.width - 280;
                const by = s.my * rect.height - 280;
                blob.style.transform = `translate3d(${bx.toFixed(1)}px, ${by.toFixed(1)}px, 0)`;
            }
            fsRafRef.current = requestAnimationFrame(tick);
        };

        const onMove = (e) => {
            const p = e.touches ? e.touches[0] : e;
            fsStateRef.current.tx = Math.max(0, Math.min(1, (p.clientX - rect.left) / rect.width));
            fsStateRef.current.ty = Math.max(0, Math.min(1, (p.clientY - rect.top) / rect.height));
        };

        stage.addEventListener('mousemove', onMove, { passive: true });
        stage.addEventListener('touchmove', onMove, { passive: true });
        fsRafRef.current = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(fsRafRef.current);
            window.removeEventListener('resize', measure);
            stage.removeEventListener('mousemove', onMove);
            stage.removeEventListener('touchmove', onMove);
        };
    }, [fullscreen]);

    return (
        <>
            <button
                type="button"
                ref={wrapRef}
                className="premium-hero__diagram-stage"
                onClick={() => setFullscreen(true)}
                aria-label="Rendszerdiagram megnyitása teljes nézetben"
            >
                <img
                    ref={imgRef}
                    className="premium-hero__diagram"
                    src={HERO_DIAGRAM_SRC}
                    alt={alt}
                    loading="eager"
                    decoding="async"
                    draggable={false}
                />
                <span ref={lensRef} className="premium-hero__diagram-lens" aria-hidden="true" />
                <span className="premium-hero__diagram-hint" aria-hidden="true">
                    <i className="ri-zoom-in-line"></i> Kattints a teljes diagramért
                </span>
            </button>

            {fullscreen && createPortal((
                <div
                    className="premium-hero__diagram-fs"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="premium-hero__diagram-fs-mesh" aria-hidden="true">
                        <span ref={fsBlobRef} className="premium-hero__diagram-fs-blob" />
                        <span className="premium-hero__diagram-fs-grain" />
                    </div>
                    <div className="premium-hero__diagram-fs-toolbar">
                        <span className="premium-hero__diagram-fs-title">
                            <i className="ri-share-2-line"></i>
                            {t('premium.home.diagram.title', { defaultValue: 'Happy Solutions modul ökoszisztéma' })}
                        </span>
                        <button type="button" onClick={() => setFullscreen(false)} aria-label="Bezárás" className="is-close"><i className="ri-close-line"></i></button>
                    </div>
                    <div
                        ref={fsStageRef}
                        className="premium-hero__diagram-fs-stage"
                    >
                        <span className="premium-hero__diagram-fs-halo" aria-hidden="true" />
                        <SystemDiagram variant="fullscreen" />
                    </div>
                    <div
                        className={`premium-hero__diagram-fs-hint${fsHintVisible ? ' is-visible' : ''}`}
                        aria-hidden="true"
                    >
                        <i className="ri-drag-move-2-line"></i>
                        <span>{t('premium.home.diagram.hint', { defaultValue: 'Vidd a kurzort egy modulra a részletekért' })}</span>
                    </div>
                </div>
            ), document.body)}
        </>
    );
};

const BannerOne = (props) => {
    const { t } = useTranslation();
    const heroRef = useRef(null);

    return (
        <div ref={heroRef} className="slider-area banner-style-1 bg-image premium-hero">
            <HeroMeshGradient scopeRef={heroRef} />
            <div className="premium-container eduvibe-animated-shape">
                <div className="premium-hero__grid">
                    <div className="premium-hero__content">
                        <span className="premium-hero__badge">
                            <i className="ri-sparkling-line" aria-hidden="true"></i>
                            {t('premium.home.heroBadge', { defaultValue: 'Magyar fejlesztésű üzleti szoftver ökoszisztéma' })}
                        </span>
                        <h1 className="title premium-hero__title">
                            {t('homeThree.hero.slogan')}
                        </h1>
                        <p className="premium-hero__lead">
                            {t('premium.home.heroLead', {
                                defaultValue: 'ERP, CRM, kereskedelmi, vendéglátó, szolgáltatói és logisztikai megoldások egy modern, integrációbarát rendszerben — hogy a vállalkozásod átláthatóbban, gyorsabban és kevesebb adminisztrációval működjön.'
                            })}
                        </p>
                        <div className="premium-hero__actions">
                            <Link className="edu-btn" to="/foglalas">
                                {t('tradePage.hero.ctaConsult', { defaultValue: 'Ingyenes konzultáció' })}
                                <i className="icon-arrow-right-line-right"></i>
                            </Link>
                            <Link className="edu-btn btn-secondary premium-btn-outline" to="/crm-rendszer">
                                {t('premium.home.heroSecondary', { defaultValue: 'Megoldások böngészése' })}
                            </Link>
                        </div>
                        <div className="premium-hero__proof" aria-label="Fő előnyök">
                            <span><i className="ri-checkbox-circle-line"></i> ERP + CRM + POS</span>
                            <span><i className="ri-checkbox-circle-line"></i> Valós idejű riportok</span>
                            <span><i className="ri-checkbox-circle-line"></i> Nyitott integrációk</span>
                        </div>
                    </div>

                    <div className="premium-hero__visual" aria-label="Happy Solutions termék előnézet">
                        <div className="premium-hero__screen">
                            <InteractiveDiagram
                                alt={t('premium.home.heroDiagramAlt', { defaultValue: 'Happy Solutions rendszer áttekintő diagram' })}
                            />
                        </div>
                        <div className="premium-floating-card premium-floating-card--top">
                            <strong>Live</strong>
                            <span>Dashboard, készlet, rendelés</span>
                        </div>
                        <div className="premium-floating-card premium-floating-card--bottom">
                            <strong>+ Integrációk</strong>
                            <span>Wolt · GLS · Shopify · NAV</span>
                        </div>
                    </div>
                </div>

                    <div className="shape-dot-wrapper shape-wrapper d-xl-block d-none">
                    <div className="shape shape-1">
                        <img src="/images/shapes/shape-01.png" alt="Shape Thumb" />
                    </div>
                    <div className="shape shape-2">
                        <img src="/images/shapes/shape-02.png" alt="Shape Thumb" />
                    </div>
                    <div className="shape shape-5">
                        <img src="/images/shapes/shape-05.png" alt="Shape Thumb" />
                    </div>
                    <div className="shape shape-6">
                        <img src="/images/shapes/shape-05-05.png" alt="Shape Thumb" />
                    </div>
                </div>

                <div className="shape-round">
                    <img src="/images/banner/banner-01/shape-27.png" alt="Shape Images" />
                </div>
            </div>
            
            {false && <div className="scroll-down-btn">
                <ScrollTo
                    className="round-btn"
                    to="about-us"
                    spy={true}
                    smooth={true}
                    duration={200}
                >
                    <i className="icon-arrow-down-s-line"></i>
                </ScrollTo>
            </div>}
        </div>

    )
}
export default BannerOne;
